import { useEffect, useMemo, useState } from "react";

import {
  useDatasetRemoveNulls,
  useMutationPatchDatasetMetadata,
  useMutationPatchDatasetSchema,
  useTranslateDatasetGeometry
} from "@/api/dataset/useDatasetAPI";
import { DatasetDetailRow } from "@/components/datasetManagement/details/columns";
import {
  SINGLE_GEOMETRY_OPTIONS,
  VALUE_TO_LABEL
} from "@/components/datasetManagement/details/dataSettingOptions";
import NotificationContent from "@/components/notification/notificationContent";
import { ROW_ID_DETAIL_DATASET } from "@/constants/dataset";
import { ERROR_MESSAGE, MESSAGE } from "@/constants/message";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import {
  DataSchemaType,
  DatasetAttributeSchema,
  DatasetMetadata,
  DatasetSchemaLabel,
  DatasetSchemaPayload
} from "@/types/dataset";
import {
  DataSettings,
  EDataSettingPointType,
  EDataSettingPopupType,
  EDataSettingSpatialType
} from "@/types/dataSetting";

import { useConfirmationModal } from "../ui/modal/useConfirmationModal";
import usePrevious from "../usePrevious";

export const MESSAGES = {
  ALL_NULLS:
    "We couldn't generate any data. Please try again or check your input.",
  GENERATION_FAILED:
    "Sorry, we're experiencing an issue while processing your request. Please try again",
  STORAGE_CAPACITY_INSUFFICIENT:
    "Storage limit reached. Clear space or upgrade to generate geometry.",
  NO_NULLS: "Country, Spatial Aggregation and Type successfully added. "
};

const PUBLISH_VALIDATION_MESSAGES = {
  DESCRIPTION: "Please add a description",
  DATA_SETTINGS: "Please set the data settings",
  REFERENCE_ID:
    "Please select ID, from table column before publish the data dataset by clicking cog icon on table header."
} as const;

export interface PublishValidationResult {
  canPublish: boolean;
  errors: {
    description?: string;
    dataSettings?: string;
    referenceId?: string;
  };
}
export interface UseDatasetDataSettingProps {
  setIsNullConfirmationOpen: (open: boolean) => void;
  setOpen: (open: boolean) => void;
  id: string | undefined;
  metadata: DatasetMetadata | undefined;
  data: DatasetDetailRow[];
  dataSetting: DataSettings | null;
  setDataSetting: React.Dispatch<React.SetStateAction<DataSettings | null>>;
  onCloseSettingPopup: () => void;
  setRefetchStream: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useDatasetDataSetting = (props: UseDatasetDataSettingProps) => {
  const {
    setIsNullConfirmationOpen,
    setOpen,
    id,
    metadata,
    data,
    dataSetting,
    setDataSetting,
    onCloseSettingPopup,
    setRefetchStream
  } = props;
  const { showToast } = useToast();
  const { showConfirmation } = useConfirmationModal();
  const prevMetadata = usePrevious(metadata);

  const [nullAmount, setNullAmount] = useState<number>(0);
  const [isHighlightNewGeometry, setIsHighlightNewGeometry] =
    useState<boolean>(false);
  const [publishValidation, setPublishValidation] =
    useState<PublishValidationResult>({ canPublish: false, errors: {} });
  const [dataSettingTemp, setDataSettingTemp] = useState<DataSettings | null>(
    dataSetting
  );

  const { mutate: mutatePatchDatasetMetadata } =
    useMutationPatchDatasetMetadata();
  const { mutate: mutatePatchDatasetSchema } = useMutationPatchDatasetSchema();
  const { mutate: mutateTranslateDatasetGeometry, isPending: isTranslating } =
    useTranslateDatasetGeometry(id as string, {
      onSuccess: (response) => {
        // TODO: @dalladirosa will be reused after Nix handle the error
        // if (response.failed === response.total) {
        //   return showToast({
        //     type: EToastType.ERROR,
        //     message: (
        //       <div className="flex flex-col gap-1">
        //         <p className="text-sm font-bold text-txtHeading">
        //           Generation Failed
        //         </p>
        //         <p className="text-sm text-txtHeading">
        //           {ERROR_MESSAGE.ALL_NULLS}
        //         </p>
        //       </div>
        //     )
        //   });
        // }
        setRefetchStream(true);

        if (
          !(
            dataSettingTemp?.spatialAggregation?.type?.value ===
              EDataSettingSpatialType.Point &&
            dataSettingTemp?.spatialAggregation?.point?.pointType?.value ===
              EDataSettingPointType.Coordinate
          ) &&
          response.failed > 0
        ) {
          setNullAmount(response.failed);
          setIsNullConfirmationOpen(true);
          return;
        }

        handleHighlightInvalidatedGeometry();

        showToast({
          type: EToastType.SUCCESS,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                Data Setting Saved
              </p>
              <p className="text-sm text-txtHeading">{MESSAGES.NO_NULLS}</p>
            </div>
          )
        });
      },
      onError(err) {
        showToast({
          type: EToastType.ERROR,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm text-txtHeading">
                {err.message.message === "Insufficient storage capacity."
                  ? MESSAGES.STORAGE_CAPACITY_INSUFFICIENT
                  : MESSAGES.GENERATION_FAILED}
              </p>
            </div>
          )
        });
      }
    });
  const { mutate: mutateDatasetRemoveNulls } = useDatasetRemoveNulls(
    id as string,
    {
      onSuccess: () => {
        setRefetchStream(true);

        setIsNullConfirmationOpen(false);
        handleHighlightInvalidatedGeometry();

        showToast({
          type: EToastType.SUCCESS,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                Data Setting Saved
              </p>
              <p className="text-sm text-txtHeading">
                Country and Spatial Aggregation successfully added, and{" "}
                {nullAmount} rows with null values have been removed.
              </p>
            </div>
          )
        });
      },
      onError: (error) => {
        showToast({
          type: EToastType.ERROR,
          message: (
            <div className="flex flex-col gap-1">
              <p className="text-sm font-bold text-txtHeading">
                Something Went Wrong
              </p>
              <p className="text-sm text-txtHeading">
                Sorry, we're experiencing an issue while processing your
                request. Please try again
              </p>
              {import.meta.env.MODE !== "production" && (
                <p>
                  <b>Server message:</b> {JSON.stringify(error.message)}
                </p>
              )}
            </div>
          )
        });
      }
    }
  );

  const formattedAttributes = (schema: DatasetAttributeSchema[]) =>
    schema.map((item) => ({
      label: item?.attribute_name
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
      value: item?.id,
      description: item?.attribute_name
    }));

  const highlightGeoColumns = useMemo(() => {
    if (!prevMetadata?.schema || !metadata?.schema) return [];

    // Get all IDs from previous metadata
    const prevIds = new Set(prevMetadata.schema.map((item) => item.id));

    // Return items from current metadata that weren't in previous metadata
    return metadata.schema.filter((item) => !prevIds.has(item.id));
  }, [metadata, prevMetadata]);

  const attributes = useMemo(
    () => formattedAttributes(metadata?.schema ?? []),
    [metadata]
  );

  const { attributesLatitude, attributesLongitude } = useMemo(() => {
    const schema = metadata?.schema ?? [];

    // Categorize attributes by data type
    // To reduce the number of iterations, we do it in a single pass
    const categorized = schema.reduce<{
      latitude: DatasetAttributeSchema[];
      longitude: DatasetAttributeSchema[];
      float: DatasetAttributeSchema[];
    }>(
      (acc, item) => {
        if (item.data_label === DatasetSchemaLabel.LATITUDE) {
          acc.latitude.push(item);
        } else if (item.data_label === DatasetSchemaLabel.LONGITUDE) {
          acc.longitude.push(item);
        } else if (item.data_type === DataSchemaType.FLOAT) {
          acc.float.push(item);
        }
        return acc;
      },
      {
        latitude: [],
        longitude: [],
        float: []
      }
    );

    // Use specific lat/long attributes if they exist, otherwise fall back to float
    const latitudeAttributes = categorized.latitude.length
      ? categorized.latitude
      : categorized.float;
    const longitudeAttributes = categorized.longitude.length
      ? categorized.longitude
      : categorized.float;

    return {
      attributesLatitude: formattedAttributes(latitudeAttributes),
      attributesLongitude: formattedAttributes(longitudeAttributes)
    };
  }, [metadata]);

  const attributesForAdminArea = useMemo(
    () =>
      formattedAttributes(
        (metadata?.schema ?? []).filter(
          (item) =>
            (item.data_type === DataSchemaType.TEXT ||
              item.data_type === DataSchemaType.TEXT2 ||
              item.data_type === DataSchemaType.INT ||
              item.data_type === DataSchemaType.ID ||
              item.data_type === DataSchemaType.TITLE ||
              item.data_type === DataSchemaType.ADDRESS) &&
            item.attribute_name !== ROW_ID_DETAIL_DATASET
        )
      ),
    [metadata]
  );

  const currentSpatialAggregationTypeFromMeta =
    metadata?.spatial_aggregation_type;
  const schemaFromMeta = metadata?.schema;

  const spatialAttributeIdFromMeta = useMemo(() => {
    return schemaFromMeta?.find((data) => data.is_reference_geo)?.id;
  }, [schemaFromMeta]);

  // To filter schema having is_spatial or geom
  const schemaIsSpatialOrGeom = useMemo(
    () => metadata?.schema?.filter((item) => item.is_spatial) ?? [],
    [metadata]
  );

  const dataSettingType = useMemo(() => {
    const geomSize = schemaIsSpatialOrGeom.length;

    if (geomSize > 1) return EDataSettingPopupType.Multiple;

    if (geomSize === 1) return EDataSettingPopupType.Single;

    return EDataSettingPopupType.None;
  }, [schemaIsSpatialOrGeom]);

  const computePublishValidation = (
    meta: DatasetMetadata | undefined
  ): PublishValidationResult => {
    if (!meta) {
      return {
        canPublish: false,
        errors: {
          description: PUBLISH_VALIDATION_MESSAGES.DESCRIPTION,
          dataSettings: PUBLISH_VALIDATION_MESSAGES.DATA_SETTINGS,
          referenceId: PUBLISH_VALIDATION_MESSAGES.REFERENCE_ID
        }
      };
    }

    if (meta.published) {
      return {
        canPublish: true,
        errors: {}
      };
    }

    const hasReferenceGeo =
      meta.schema?.some((column) => column.is_reference_geo) ?? false;
    const hasReferenceId =
      meta.schema?.some((column) => column.is_reference_id) ?? false;

    const errors: PublishValidationResult["errors"] = {};

    if (!meta.short_description?.trim()) {
      errors.description = PUBLISH_VALIDATION_MESSAGES.DESCRIPTION;
    }

    const hasDataSettingsConfigured =
      !!meta.country_id && !!meta.spatial_aggregation_type;

    if (!(hasDataSettingsConfigured && hasReferenceGeo)) {
      errors.dataSettings = PUBLISH_VALIDATION_MESSAGES.DATA_SETTINGS;
    }

    if (!hasReferenceId) {
      errors.referenceId = PUBLISH_VALIDATION_MESSAGES.REFERENCE_ID;
    }

    return {
      canPublish: Object.keys(errors).length === 0,
      errors
    };
  };

  const refreshPublishValidation = () => {
    const nextValidation = computePublishValidation(metadata);
    setPublishValidation(nextValidation);

    if (!nextValidation.canPublish) return false;

    return true;
  };

  const handleUnpublishDataset = () => {
    if (!id) {
      showToast({
        type: EToastType.ERROR,
        message: "ID is required to unpublish dataset"
      });
      return;
    }
    mutatePatchDatasetMetadata(
      { id, published: false },
      {
        onSuccess: () => {
          setRefetchStream(true);
          showToast({
            type: EToastType.SUCCESS,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Dataset Unpublished Successfully
                </p>
                <p className="text-sm text-txtHeading">
                  Your dataset is no longer published and is not available for
                  analysis
                </p>
              </div>
            ),
            autoClose: 2000
          });
        },
        onError() {
          showToast({
            type: EToastType.ERROR,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Something Went Wrong
                </p>
                <p className="text-sm text-txtHeading">
                  Sorry, we're experiencing an issue while unpublishing your
                  dataset. Please try again.
                </p>
              </div>
            )
          });
        },
        onSettled() {
          setOpen(false);
        }
      }
    );
  };

  const handlePublishDataset = () => {
    if (!id) {
      showToast({
        type: EToastType.ERROR,
        message: "ID is required to publish dataset"
      });
      return;
    }

    mutatePatchDatasetMetadata(
      { id, published: true },
      {
        onSuccess: () => {
          setRefetchStream(true);
          showToast({
            type: EToastType.SUCCESS,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Dataset Published Successfully
                </p>
                <p className="text-sm text-txtHeading">
                  Your data is now published and available for analysis.
                </p>
              </div>
            ),
            autoClose: 2000
          });
        },
        onError() {
          showToast({
            type: EToastType.ERROR,
            message: (
              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-txtHeading">
                  Something Went Wrong
                </p>
                <p className="text-sm text-txtHeading">
                  Sorry, we're experiencing an issue while publishing your
                  dataset. Please try again.
                </p>
              </div>
            ),
            autoClose: 2000
          });
        },
        onSettled() {
          setOpen(false);
        }
      }
    );
  };

  const handleSaveDataSettingExistGeom = () => {
    if (!id || !dataSettingTemp?.country?.value) return;
    mutatePatchDatasetMetadata(
      {
        id,
        country_id: dataSettingTemp?.country?.value,
        spatial_aggregation_type:
          dataSettingTemp?.spatialAggregation?.type?.value
      },
      {
        onSuccess: () => {
          setDataSetting(dataSettingTemp);
          setRefetchStream(true);

          showToast({
            type: EToastType.SUCCESS,
            message: (
              <NotificationContent
                title={MESSAGE.DATA_SETTING_SAVED}
                description={MESSAGE.SAVE_DATA_SETTING_SUCCESS}
              />
            )
          });
        },
        onError: () => {
          showToast({
            type: EToastType.ERROR,
            message: (
              <NotificationContent
                title={ERROR_MESSAGE.SOMETHING_WENT_WRONG}
                description={ERROR_MESSAGE.SOMETHING_WENT_WRONG_DESCRIPTION}
              />
            )
          });
        }
      }
    );
    // With type Multiple, need to call api to set as the primary/reference geo column
    if (dataSettingType !== EDataSettingPopupType.Multiple) return;

    const isRefGeoAttribute = () => {
      switch (dataSettingTemp?.spatialAggregation?.type?.value) {
        case EDataSettingSpatialType.Point:
          return dataSettingTemp?.spatialAggregation?.pointAttribute;
        case EDataSettingSpatialType.Polygon:
          return dataSettingTemp?.spatialAggregation?.polygonAttribute;
        case EDataSettingSpatialType.Line:
          return dataSettingTemp?.spatialAggregation?.lineAttribute;
        case EDataSettingSpatialType.H3:
          return dataSettingTemp?.spatialAggregation?.h3Attribute;
        case EDataSettingSpatialType.Geohash:
          return dataSettingTemp?.spatialAggregation?.geohashAttribute;
        default:
          return dataSettingTemp?.spatialAggregation?.pointAttribute;
      }
    };

    const attributeSelected = metadata?.schema.find(
      (item) => item.id === isRefGeoAttribute()
    ) as DatasetSchemaPayload;

    mutatePatchDatasetSchema({
      ...attributeSelected,
      id: attributeSelected.id,
      is_reference_geo: !!isRefGeoAttribute()
    });
  };

  const handlePublishButtonClick = () => {
    const isPublishable = refreshPublishValidation();

    if (!isPublishable) return false;

    return true;
  };

  const handleSaveDataSetting = () => {
    if (!dataSettingType || !id) return;
    // Check dataset is published or not, if published will show popup to confirm unpublish before change
    if (metadata?.published) {
      showConfirmation({
        title: "Unpublish Dataset?",
        message:
          MESSAGE.DATASET_MANAGEMENT_UNPUBLISH_DATASET_CONFIRMATION_DESCRIPTION,
        cancelLabel: "",
        confirmLabel: "Unpublish",
        onConfirm: () => handleUnpublishDataset()
      });
      return;
    }
    // Case dataset unpublished
    onCloseSettingPopup();

    if (
      [EDataSettingPopupType.Single, EDataSettingPopupType.Multiple].includes(
        dataSettingType
      )
    ) {
      handleSaveDataSettingExistGeom();
      return;
    }

    if (dataSettingType === EDataSettingPopupType.None) {
      handleGenerateGeometry();
    }
  };

  const getAttributeIds = () => {
    const { spatialAggregation } = dataSettingTemp as DataSettings;
    let attributeIds: (string | undefined)[] = [];

    if (spatialAggregation?.type?.value === EDataSettingSpatialType.Point) {
      const pointType = spatialAggregation.point?.pointType?.value;

      if (pointType === "geocoding") {
        attributeIds = [spatialAggregation.point?.geocodingAtrtribute];
      } else {
        attributeIds = [
          spatialAggregation.point?.pointCoordinate?.latAttribute?.value,
          spatialAggregation.point?.pointCoordinate?.longAttribute?.value
        ];
      }
    } else {
      attributeIds = [spatialAggregation?.adminAreaAttribute];
    }

    return attributeIds;
  };

  const handleGenerateGeometry = () => {
    if (!dataSettingTemp) return;

    const { spatialAggregation, country } = dataSettingTemp;

    const attributeIds = getAttributeIds();
    const AGGREGATION_TYPE = {
      poi: "poi",
      admin_area: "adm_area"
    };

    mutateTranslateDatasetGeometry({
      geocoding:
        spatialAggregation?.type?.value === EDataSettingSpatialType.Point &&
        spatialAggregation?.point?.pointType?.value ===
          EDataSettingPointType.Geocoding,
      source_attribute_ids: attributeIds as string[],
      country_id: country?.value as string,
      spatial_aggregation_type:
        AGGREGATION_TYPE[
          spatialAggregation?.type?.value as "poi" | "admin_area"
        ]
    });
  };

  const handleRemoveNulls = () => {
    mutateDatasetRemoveNulls({
      attribute_id: highlightGeoColumns[0].id
    });
  };

  const handleHighlightInvalidatedGeometry = () => {
    setIsHighlightNewGeometry(true);
  };

  /* If it has a single attribute, the system will automatically determine the appropriate
     spatial aggregation based on the geometry type and its attribute (user done need to select).
     - Point, if the geometry value is POINT(30 10)
     - Polygon, if the geometry value is POLYGON ((30 10, 40 40, 20 40, 10 20, 30 10)) or MULTIPOLYGON (((30 20, 45 40, 10 40, 30 20)), ((15 5, 40 10, 20 20, 15))
  */
  useEffect(() => {
    if (dataSettingType !== EDataSettingPopupType.Single) return;

    const mainSpatialAttribute = schemaIsSpatialOrGeom[0];

    if (!mainSpatialAttribute) return;

    let newAggregationTypeOption = SINGLE_GEOMETRY_OPTIONS[0];

    // Option 1: If metadata already specifies a spatial_aggregation_type, respect it.
    if (metadata?.spatial_aggregation_type) {
      newAggregationTypeOption =
        SINGLE_GEOMETRY_OPTIONS.find(
          (item) => item.value === metadata.spatial_aggregation_type
        ) || SINGLE_GEOMETRY_OPTIONS[0];
    } else if (
      data?.length > 0 &&
      data[0] &&
      typeof data[0][mainSpatialAttribute.attribute_name] === "string"
    ) {
      // Option 2: Infer from the first row of data if metadata doesn't specify.
      const geometryValue = data[0][
        mainSpatialAttribute.attribute_name
      ] as string;
      if (
        geometryValue.includes("MULTIPOLYGON") ||
        geometryValue.includes("POLYGON")
      ) {
        // Assuming SINGLE_GEOMETRY_OPTIONS[1] is for Polygon types
        newAggregationTypeOption = SINGLE_GEOMETRY_OPTIONS[1];
      } else if (geometryValue.includes("POINT")) {
        // Assuming SINGLE_GEOMETRY_OPTIONS[0] is for Point types
        newAggregationTypeOption = SINGLE_GEOMETRY_OPTIONS[0];
      }
    }

    // Only update if the new type is different from the current one in dataSettingTemp
    // and newAggregationTypeOption is actually found.
    if (
      newAggregationTypeOption &&
      newAggregationTypeOption.value !==
        dataSettingTemp?.spatialAggregation?.type?.value
    ) {
      setDataSettingTemp((prev) => ({
        ...prev, // Handle null initial state for prev
        spatialAggregation: {
          ...prev?.spatialAggregation,
          type: newAggregationTypeOption
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSettingType, data, metadata, schemaIsSpatialOrGeom]);

  // This is for Multiple Geometry Setting, to handle the default aggregation type
  useEffect(() => {
    if (dataSettingType !== EDataSettingPopupType.Multiple) return;

    setDataSettingTemp((prev) => ({
      ...prev, // Handle null initial state for prev
      spatialAggregation: {
        ...prev?.spatialAggregation,
        type: {
          label:
            VALUE_TO_LABEL[
              (currentSpatialAggregationTypeFromMeta as
                | "poi"
                | "polygon"
                | "adm_area"
                | "line"
                | "H3"
                | "geohash") ?? "polygon"
            ],
          value: currentSpatialAggregationTypeFromMeta ?? "polygon"
        },
        ...(currentSpatialAggregationTypeFromMeta === "poi"
          ? { pointAttribute: spatialAttributeIdFromMeta }
          : { polygonAttribute: spatialAttributeIdFromMeta })
      }
    }));
  }, [
    dataSettingType,
    currentSpatialAggregationTypeFromMeta,
    spatialAttributeIdFromMeta
  ]);

  useEffect(() => {
    if (!isHighlightNewGeometry) return;

    const timeout = setTimeout(() => {
      setIsHighlightNewGeometry(false);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isHighlightNewGeometry]);

  return {
    nullAmount,
    dataSettingTemp,
    dataSettingType,
    attributes,
    attributesLatitude,
    attributesLongitude,
    attributesForAdminArea,
    highlightGeoColumns,
    isHighlightNewGeometry,
    isTranslating,
    setDataSettingTemp,
    handleHighlightInvalidatedGeometry,
    handleSaveDataSetting,
    handlePublishDataset,
    handleUnpublishDataset,
    handleRemoveNulls,
    publishValidation,
    handlePublishButtonClick
  };
};
